var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __values = (this && this.__values) || function(o) {
    var s = typeof Symbol === "function" && Symbol.iterator, m = s && o[s], i = 0;
    if (m) return m.call(o);
    if (o && typeof o.length === "number") return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
    throw new TypeError(s ? "Object is not iterable." : "Symbol.iterator is not defined.");
};
import { createSelector } from "@reduxjs/toolkit";
import { systemDataConstants } from "api/system-data";
import { SpecialGroupLayout, } from "api/types";
import { isCentralizedSystem, getCupComponent, getVacuumPumpComponent, } from "store/system-data/helpers";
import { cardboardParameterNames } from "api/tacton/constants";
export var selectSystemDataRootDefineParameters = function (state) {
    return state.systemDataDefineParameters;
};
export var selectSystemDataDefineParameters = function (state) {
    return selectSystemDataRootDefineParameters(state).dataState.data;
};
export var selectIsLoadingDefineParameters = function (state) {
    return selectSystemDataRootDefineParameters(state).dataState.isLoading;
};
export var selectErrorsDefineParameters = function (state) {
    return selectSystemDataRootDefineParameters(state).dataState.errors;
};
export var selectCupSelectionMap = function (state) {
    return selectSystemDataRootDefineParameters(state).cupSelectionMap;
};
export var selectCupMountingSelectionMap = function (state) {
    return selectSystemDataRootDefineParameters(state).cupMountingSelectionMap;
};
export var selectCupTechnologySelectionMap = function (state) {
    return selectSystemDataRootDefineParameters(state).cupTechnologySelectionMap;
};
export var selectPumpSelectionDecentralMap = function (state) {
    return selectSystemDataRootDefineParameters(state).pumpSelectionDecentralMap;
};
export var selectPumpSelectionCentralMap = function (state) {
    return selectSystemDataRootDefineParameters(state).pumpSelectionCentralMap;
};
export var selectPendingParameters = function (state) { var _a; return (_a = selectSystemDataRootDefineParameters(state).dataState.pending) === null || _a === void 0 ? void 0 : _a.parameters; };
export var selectPendingSelections = function (state) { var _a; return (_a = selectSystemDataRootDefineParameters(state).dataState.pending) === null || _a === void 0 ? void 0 : _a.selections; };
export var selectManualCalculationInput = function (state) {
    return state.systemDataDefineParameters.manualCalculationInput;
};
export var selectConflict = createSelector(selectSystemDataDefineParameters, function (systemData) { return systemData === null || systemData === void 0 ? void 0 : systemData.conflict; });
export var selectOutputDefineParameters = createSelector(selectSystemDataDefineParameters, function (systemData) { return systemData === null || systemData === void 0 ? void 0 : systemData.output; });
export var selectIsCentralizedSystem = createSelector(selectSystemDataDefineParameters, function (systemData) { return isCentralizedSystem(systemData); });
export var selectUseDoubleCompressedAirTubes = createSelector(selectSystemDataDefineParameters, function (systemData) {
    return systemData
        ? systemData.parameters[systemDataConstants.parameterNames.compressedAirConnections].value ===
            systemDataConstants.parameterValues.doubleCompressedAirConnections
        : false;
});
export var selectPartSecuredLevel = createSelector(selectSystemDataDefineParameters, function (systemData) {
    var _a;
    return Number((_a = systemData === null || systemData === void 0 ? void 0 : systemData.parameters[systemDataConstants.parameterNames.partSecuredLevel]) === null || _a === void 0 ? void 0 : _a.value) || 0;
});
export var selectEnergySavingLevel = createSelector(selectSystemDataDefineParameters, function (systemData) {
    var _a;
    return Number((_a = systemData === null || systemData === void 0 ? void 0 : systemData.parameters[systemDataConstants.parameterNames.energySavingLevel]) === null || _a === void 0 ? void 0 : _a.value) || 0;
});
/** Whether the Tacton "energy saving" system parameter is switched on. */
export var selectIsEnergySavingParameterOn = createSelector(selectSystemDataDefineParameters, function (systemData) {
    var _a;
    return ((_a = systemData === null || systemData === void 0 ? void 0 : systemData.parameters[systemDataConstants.parameterNames.energySavingToggle]) === null || _a === void 0 ? void 0 : _a.value) === systemDataConstants.parameterValues.energySavingOn;
});
/* The Tacton parameters that narrow down which pumps are offered, shown in
 * the pump drawers as well as under "Vacuum pump parameters". Hidden ones
 * are not in `parameters`, so they are left out. */
var pumpFilterParameterNames = [
    systemDataConstants.parameterNames.compressedAirConnections,
    systemDataConstants.parameterNames.energySavingToggle,
];
export var selectPumpFilterParameters = createSelector(selectSystemDataDefineParameters, function (systemData) {
    return pumpFilterParameterNames
        .map(function (name) { return systemData === null || systemData === void 0 ? void 0 : systemData.parameters[name]; })
        .filter(function (parameter) { return parameter !== undefined; });
});
export var selectSelectedCup = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectCupSelectionMap, function (systemData, cupSelectionMap) {
        return getCupComponent(nodeId, cupSelectionMap, systemData);
    });
};
/*
 * Every node's selected suction-cup component, keyed by node id. Nodes with no
 * cup selection (or whose selection resolves to no component) are absent. Used
 * by the geometric cup-to-box assignment and the fill tool, which need each
 * cup's footprint.
 */
export var selectCupComponentsByNode = createSelector(selectSystemDataDefineParameters, selectCupSelectionMap, function (systemData, cupSelectionMap) {
    var cups = {};
    Object.keys(cupSelectionMap).forEach(function (nodeId) {
        var cup = getCupComponent(nodeId, cupSelectionMap, systemData);
        if (cup)
            cups[nodeId] = cup;
    });
    return cups;
});
export var selectSelectedCups = function (nodeIds) {
    return createSelector(selectSystemDataDefineParameters, selectCupSelectionMap, function (systemData, cupSelectionMap) {
        return nodeIds.map(function (nodeId) {
            return getCupComponent(nodeId, cupSelectionMap, systemData);
        });
    });
};
var getCupMountings = function (nodeIds, systemData, cupMountingSelectionMap) {
    if (!systemData)
        return [];
    var selectionIds = nodeIds.map(function (nodeId) { return cupMountingSelectionMap[nodeId]; });
    var values = selectionIds.map(function (selectionId) { return systemData.cupMountingSelections[selectionId]; });
    return values.map(function (value) { return (value && systemData.cupMountingComponents[value]) || undefined; });
};
export var selectSelectedCupMountings = function (nodeIds) {
    return createSelector(selectSystemDataDefineParameters, selectCupMountingSelectionMap, function (systemData, cupMountingSelectionMap) {
        return getCupMountings(nodeIds, systemData, cupMountingSelectionMap);
    });
};
export var selectSelectedCupMounting = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectCupMountingSelectionMap, function (systemData, cupMountingSelectionMap) {
        var mountings = getCupMountings([nodeId], systemData, cupMountingSelectionMap);
        return mountings.length > 0 ? mountings[0] : undefined;
    });
};
export var getSelectedCupTechnology = function (systemData, cupTechnologySelectionMap, nodeId) {
    var selectionId = cupTechnologySelectionMap[nodeId];
    if (!selectionId)
        return;
    var value = systemData.cupTechnologySelections[selectionId];
    if (!value)
        return;
    return systemData.cupTechnologyComponents[value] || undefined;
};
export var selectSelectedCupTechnology = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectCupTechnologySelectionMap, function (systemData, cupTechnologySelectionMap) {
        return systemData
            ? getSelectedCupTechnology(systemData, cupTechnologySelectionMap, nodeId)
            : undefined;
    });
};
export var selectSelectedCupTechnologies = function (nodeIds) {
    return createSelector(selectSystemDataDefineParameters, selectCupTechnologySelectionMap, function (systemData, cupTechnologySelectionMap) {
        return systemData
            ? nodeIds.map(function (nodeId) {
                return getSelectedCupTechnology(systemData, cupTechnologySelectionMap, nodeId);
            })
            : [];
    });
};
var selectedDecentralPumps = function (nodeIds, systemData, pumpSelectionDecentralMap) {
    if (!systemData || !systemData.vacuumPumpSelectionsDecentralized)
        return [];
    var selectionIds = nodeIds.map(function (nodeId) { return pumpSelectionDecentralMap[nodeId]; });
    var values = selectionIds.map(function (selectionId) { return systemData.vacuumPumpSelectionsDecentralized[selectionId]; });
    return values.map(function (value) { return (value && systemData.vacuumPumpComponents[value]) || undefined; });
};
export var selectSelectedDecentralPump = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectPumpSelectionDecentralMap, function (systemData, pumpSelectionDecentralMap) {
        var selected = selectedDecentralPumps([nodeId], systemData, pumpSelectionDecentralMap);
        return selected.length > 0 ? selected[0] : undefined;
    });
};
export var selectSelectedDecentralPumps = function (nodeIds) {
    return createSelector(selectSystemDataDefineParameters, selectPumpSelectionDecentralMap, function (systemData, pumpSelectionDecentralMap) {
        return selectedDecentralPumps(nodeIds, systemData, pumpSelectionDecentralMap);
    });
};
/**
 * True for a grip position where the user hasn't picked anything at all — no
 * cup, cup technology, cup mounting or decentral pump. Note that this is
 * stricter than `selectEmptyGripPositionIds`, which flags grip positions that
 * are missing a component *required* for a calculation (cup, and pump in a
 * decentralized system) regardless of what else has been picked. Only
 * unconfigured grip positions are offered for automatic removal: one with a
 * cup but no pump yet still holds the user's work.
 */
export var isGripPositionUnconfigured = function (nodeId, defineParameters) {
    var systemData = defineParameters.dataState.data;
    return (!getCupComponent(nodeId, defineParameters.cupSelectionMap, systemData) &&
        !(systemData &&
            getSelectedCupTechnology(systemData, defineParameters.cupTechnologySelectionMap, nodeId)) &&
        !getCupMountings([nodeId], systemData, defineParameters.cupMountingSelectionMap)[0] &&
        !selectedDecentralPumps([nodeId], systemData, defineParameters.pumpSelectionDecentralMap)[0]);
};
/**
 * The first component a grip position still needs before it can be
 * calculated: its cup, then — in a decentralized system, where the pump sits
 * on the grip position — its pump. Undefined when nothing is missing, and
 * while the system data has not loaded (until then every slot reads empty).
 */
export var getMissingGripPositionComponent = function (nodeId, defineParameters) {
    var systemData = defineParameters.dataState.data;
    if (!systemData)
        return undefined;
    var cupSelectionId = defineParameters.cupSelectionMap[nodeId];
    if (!systemData.suctionCupSelections[cupSelectionId])
        return "cup";
    // Pumps are their own nodes in a centralized system.
    if (isCentralizedSystem(systemData))
        return undefined;
    var pumpSelectionId = defineParameters.pumpSelectionDecentralMap[nodeId];
    return systemData.vacuumPumpSelectionsDecentralized[pumpSelectionId]
        ? undefined
        : "pump";
};
export var selectMissingGripPositionComponent = function (nodeId) {
    return createSelector(selectSystemDataRootDefineParameters, function (defineParameters) {
        return getMissingGripPositionComponent(nodeId, defineParameters);
    });
};
export var selectSelectedCentralPump = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectPumpSelectionCentralMap, function (systemData, pumpSelectionCentralMap) {
        if (!systemData)
            return;
        var selectionId = pumpSelectionCentralMap[nodeId];
        if (!selectionId)
            return;
        var value = systemData.vacuumPumpSelectionsCentralized[selectionId];
        if (!value)
            return;
        return systemData.vacuumPumpComponents[value] || undefined;
    });
};
export var selectSelectedCentralPumps = function (nodeIds) {
    return createSelector(selectSystemDataDefineParameters, selectPumpSelectionCentralMap, function (systemData, pumpSelectionCentralMap) {
        if (!systemData)
            return [];
        var result = nodeIds.map(function (nodeId) {
            var selectionId = pumpSelectionCentralMap[nodeId];
            if (!selectionId)
                return undefined;
            var value = systemData.vacuumPumpSelectionsCentralized[selectionId];
            if (!value)
                return undefined;
            return systemData.vacuumPumpComponents[value] || undefined;
        });
        return result;
    });
};
export var selectHasAvailableSelectionsForGripPosition = createSelector(selectSystemDataDefineParameters, selectCupSelectionMap, selectPumpSelectionDecentralMap, selectCupMountingSelectionMap, selectCupTechnologySelectionMap, function (systemData, cupSelectionMap, pumpSelectionDecentralMap, cupMountingSelectionMap, cupTechnologySelectionMap) {
    if (!systemData)
        return false;
    return (Object.keys(systemData.suctionCupSelections).some(function (selection) { return !Object.values(cupSelectionMap).includes(selection); }) &&
        Object.keys(systemData.vacuumPumpSelectionsDecentralized).some(function (selection) {
            return !Object.values(pumpSelectionDecentralMap).includes(selection);
        }) &&
        Object.keys(systemData.cupMountingSelections).some(function (selection) {
            return !Object.values(cupMountingSelectionMap).includes(selection);
        }) &&
        Object.keys(systemData.cupTechnologySelections).some(function (selection) {
            return !Object.values(cupTechnologySelectionMap).includes(selection);
        }));
});
export var selectHasAvailableSelectionsForPump = createSelector(selectSystemDataDefineParameters, selectPumpSelectionCentralMap, function (systemData, pumpSelectionCentralMap) {
    if (!systemData)
        return false;
    return Object.keys(systemData.vacuumPumpSelectionsCentralized).some(function (selection) {
        return !Object.values(pumpSelectionCentralMap).includes(selection);
    });
});
export var selectHasCupSelection = createSelector(selectSystemDataDefineParameters, selectCupSelectionMap, function (systemData, cupSelectionMap) {
    if (!systemData)
        return false;
    return Object.values(cupSelectionMap).some(function (selection) { return systemData.suctionCupSelections[selection]; });
});
export var selectHasPumpSelection = createSelector(selectSystemDataDefineParameters, selectPumpSelectionCentralMap, selectPumpSelectionDecentralMap, function (systemData, pumpSelectionCentralMap, pumpSelectionDecentralMap) {
    if (!systemData)
        return false;
    return (Object.values(pumpSelectionCentralMap).some(function (selection) { return systemData.vacuumPumpSelectionsCentralized[selection]; }) ||
        Object.values(pumpSelectionDecentralMap).some(function (selection) { return systemData.vacuumPumpSelectionsDecentralized[selection]; }));
});
export var selectGroupedParametersDefineParameters = createSelector(selectSystemDataDefineParameters, function (systemData) {
    if (!systemData)
        return [];
    return Object.values(systemData.parameterGroups).map(function (group) {
        return __assign(__assign({}, group), { parameters: Object.values(systemData.parameters).filter(function (parameter) { return parameter.groupId === group.id; }) });
    });
});
export var selectParametersGroupedInSubGroups = function (parentGroupIds) {
    return createSelector(selectSystemDataDefineParameters, selectGroupedParametersDefineParameters, function (systemData, allGroups) {
        var e_1, _a, e_2, _b;
        var groups = allGroups.filter(function (group) { return parentGroupIds.includes(group.id) && group.parameters; });
        if (groups.length === 0) {
            return [];
        }
        var subGroups = systemData ? systemData.parameterSubGroups : [];
        var parametersAndGroups = [];
        try {
            for (var groups_1 = __values(groups), groups_1_1 = groups_1.next(); !groups_1_1.done; groups_1_1 = groups_1.next()) {
                var group = groups_1_1.value;
                var _loop_1 = function (param) {
                    var belongsToSubGroup = subGroups.find(function (subGroup) {
                        return subGroup.memberIds.includes(param.id);
                    });
                    if (!belongsToSubGroup) {
                        parametersAndGroups.push(param);
                        return "continue";
                    }
                    var existingSubGroup = parametersAndGroups.find(function (parameterOrGroup) { return parameterOrGroup.id === belongsToSubGroup.id; });
                    if (existingSubGroup) {
                        existingSubGroup.parameters.push(param);
                    }
                    else {
                        var newSubGroup = {
                            id: belongsToSubGroup.id,
                            parameters: [param],
                            description: belongsToSubGroup.description,
                            properties: belongsToSubGroup.properties,
                        };
                        parametersAndGroups.push(newSubGroup);
                    }
                };
                try {
                    for (var _c = (e_2 = void 0, __values(group.parameters)), _d = _c.next(); !_d.done; _d = _c.next()) {
                        var param = _d.value;
                        _loop_1(param);
                    }
                }
                catch (e_2_1) { e_2 = { error: e_2_1 }; }
                finally {
                    try {
                        if (_d && !_d.done && (_b = _c.return)) _b.call(_c);
                    }
                    finally { if (e_2) throw e_2.error; }
                }
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (groups_1_1 && !groups_1_1.done && (_a = groups_1.return)) _a.call(groups_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
        return parametersAndGroups;
    });
};
export var selectIsMultiCircuitSystem = createSelector(selectIsCentralizedSystem, selectPumpSelectionCentralMap, function (isCentralizedSystem, pumpSelectionCentralMap) {
    if (!isCentralizedSystem)
        return false;
    return Object.values(pumpSelectionCentralMap).length > 1;
});
export var selectVacuumPumpComponent = function (nodeId) {
    return createSelector(selectSystemDataDefineParameters, selectPumpSelectionCentralMap, selectPumpSelectionDecentralMap, function (systemData, pumpSelectionCentralMap, pumpSelectionDecentralMap) {
        return getVacuumPumpComponent(nodeId, pumpSelectionCentralMap, pumpSelectionDecentralMap, systemData);
    });
};
/* ------------------------------------------ *
 * ------ CARDBOARD Specific Selectors ------ *
 * ------------------------------------------ */
//#region Cardboard
export var selectGroupedParametersDefineCardboard = createSelector(selectGroupedParametersDefineParameters, function (groupedParameters) {
    return groupedParameters.find(function (group) { return group.specialGroupLayout === SpecialGroupLayout.CARDBOARD; });
});
export var selectPaperCoatingValue = createSelector(selectSystemDataDefineParameters, function (systemData) {
    var _a;
    if (!systemData)
        return;
    return (_a = systemData.parameters[cardboardParameterNames.paperCoating]) === null || _a === void 0 ? void 0 : _a.value;
});
export var selectTapeParameter = createSelector(selectSystemDataDefineParameters, function (systemData) {
    var _a;
    if (!systemData)
        return;
    return (_a = systemData.parameters[cardboardParameterNames.cardboardTape]) === null || _a === void 0 ? void 0 : _a.value;
});
export var selectTapeEndsParameter = createSelector(selectSystemDataDefineParameters, function (systemData) {
    if (!systemData)
        return;
    return systemData.parameters[cardboardParameterNames.cardboardTapeEnds];
});
export var selectCardboardCodeParameter = createSelector(selectSystemDataDefineParameters, function (systemData) {
    if (!systemData)
        return;
    return systemData.parameters[cardboardParameterNames.cardboardCode];
});
export var selectHasStartedBuildingSystem = createSelector(selectPendingSelections, selectSystemDataDefineParameters, function (pendingSelections, systemData) {
    var hasPendingSelections = !!pendingSelections && Object.values(pendingSelections).length > 0;
    var selections = [
        systemData === null || systemData === void 0 ? void 0 : systemData.suctionCupSelections,
        systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpSelectionsCentralized,
        systemData === null || systemData === void 0 ? void 0 : systemData.vacuumPumpSelectionsDecentralized,
        systemData === null || systemData === void 0 ? void 0 : systemData.cupMountingSelections,
    ];
    var hasCommittedAnyValues = Boolean(systemData &&
        Object.values(systemData.parameters).some(function (parameter) { return parameter.committed; }));
    var hasSelections = selections
        .flatMap(function (selection) { return (selection ? Object.values(selection) : []); })
        .some(function (value) { return value !== null; });
    return hasPendingSelections || hasSelections || hasCommittedAnyValues;
});
